import {
  includes,
  intersection,
  map,
  max,
  reduce,
  size,
  uniq,
  without,
} from "lodash";
import { Facet } from "@linkfluence/models/facet";
import {
  Dataviz,
  DatavizEmbedded,
  DatavizName,
  getDefaultDataviz,
  getHeightFromDataviz,
  isAIDataviz,
  isDemographicDataviz,
  isEmbeddedDataviz,
  isEmvDataviz,
  isPinnedAuthorDataviz,
  isPinnedPostDataviz,
  isTextDataviz,
} from "../dataviz";
import { FullFacet, isClosedCustomField } from "../facets";
import {
  isAICard,
  isClassificationCard,
  isClusterCard,
  isDateMetricCard,
  isDateMetricsCard,
  isDateTermCard,
  isDemographicCard,
  isEmbeddedCard,
  isEmvCard,
  isGeoCard,
  isPinnedAuthorCard,
  isPinnedPostCard,
  isPivotCard,
  isPostsCard,
  isTermCard,
  isTermMetricsCard,
  isTextCard,
  isUsersCard,
} from "./is";
import {
  AICard,
  Card,
  ClassificationCard,
  ClusterCard,
  DateMetricCard,
  DateMetricsCard,
  DateTermCard,
  DemographicCard,
  EmbeddedCard,
  EmvCard,
  GeoCard,
  PinnedAuthorCard,
  PinnedPostCard,
  PivotCard,
  PostsCard,
  TermCard,
  TermMetricsCard,
  TextCard,
  UsersCard,
} from "./model";
import {
  getTermFromCard,
  isDateTermCardTerm,
  isPivotCardTerm,
  isSampleTerm,
  isTermCardTerm,
  isTermMetricsCardTerm,
} from "./term";

export function getMaxHeightFromCards(cards: Card[]) {
  const isSingleLine = size(cards) === 1;
  return max(
    map(cards, (card) =>
      card ? getHeightFromDataviz(card.dataviz, isSingleLine) : 0
    )
  );
}

export function getAccessibleDatavizFromCard(
  card: Card,
  facets: Record<string, FullFacet>
): Dataviz["name"][] {
  if (
    isDateTermCard(card) ||
    isPivotCard(card) ||
    isTermCard(card) ||
    isTermMetricsCard(card)
  ) {
    const term = getTermFromCard(card);
    let viz = [];
    if (isTermCardTerm(term as Facet, facets[term])) {
      viz = viz.concat(getTermCardDataviz());
      if (isSampleTerm(term as Facet, facets[term])) {
        viz = without(viz, DatavizName.DoughnutChart);
      }
    }
    if (isPivotCardTerm(term as Facet, facets[term])) {
      viz = viz.concat(getPivotCardDataviz());
    }
    if (isDateTermCardTerm(term as Facet, facets[term])) {
      viz = viz.concat(getDateTermCardDataviz());
    }
    if (isTermMetricsCardTerm(term as Facet, facets[term])) {
      viz = viz.concat(getTermMetricsCardDataviz());
    }
    return uniq(viz);
  } else if (isDateMetricCard(card) || isDateMetricsCard(card)) {
    return uniq([...getDateMetricDataviz(), ...getDateMetricsCardDataviz()]);
  } else {
    return getDatavizFromCard(card);
  }
}

export function getDateTermCardDataviz() {
  return [
    DatavizName.StackedBarChart,
    DatavizName.MultipleLineChart,
    DatavizName.StreamChart,
  ];
}

export function getDateMetricsCardDataviz() {
  return [DatavizName.DoubleTimeChart];
}

export function getTermCardDataviz() {
  return [
    DatavizName.HorizontalBarChart,
    DatavizName.DoughnutChart,
    DatavizName.Cloud,
  ];
}

export function getTermMetricsCardDataviz() {
  return [DatavizName.BubbleChart, DatavizName.ScatterPlot];
}

export function getDateMetricDataviz() {
  return [DatavizName.Kpi, DatavizName.LineChart];
}

export function getUsersCardDataviz() {
  return [
    DatavizName.TopAuthors,
    DatavizName.HorizontalBarChart,
    DatavizName.Cloud,
  ];
}

export function getPostsCardDataviz() {
  return [DatavizName.TopPosts, DatavizName.PictureWall];
}

export function getClusterCardDataviz() {
  return [DatavizName.ImageClustering];
}

export function getGeoCardDataviz() {
  return [DatavizName.HorizontalBarChart, DatavizName.Map];
}

export function getPivotCardDataviz() {
  return [DatavizName.HorizontalStackedBarChart];
}

export function getClassificationCardDataviz() {
  return [
    DatavizName.HorizontalBarChart,
    DatavizName.Cloud,
    DatavizName.Sunburst,
    DatavizName.TreeMap,
  ];
}

export function getDemographicCardDataviz() {
  return [DatavizName.Demographic];
}

export function getEmbeddedCardDataviz() {
  return [DatavizName.Embedded];
}

export function getTextCardDataviz() {
  return [DatavizName.Text];
}

export function getPinnedPostDataviz() {
  return [DatavizName.PinnedPost];
}
export function getPinnedAuthorDataviz() {
  return [DatavizName.PinnedAuthor];
}

export function getEmvCardDataviz() {
  return [DatavizName.Emv];
}

export function getAICardDataviz() {
  return [DatavizName.AI];
}

export function getDatavizFromCard(card: Card): Dataviz["name"][] {
  if (isDateTermCard(card)) {
    return getDateTermCardDataviz();
  } else if (isTermCard(card)) {
    return getTermCardDataviz();
  } else if (isDateMetricCard(card)) {
    return getDateMetricDataviz();
  } else if (isUsersCard(card)) {
    return getUsersCardDataviz();
  } else if (isPostsCard(card)) {
    return getPostsCardDataviz();
  } else if (isPivotCard(card)) {
    return getPivotCardDataviz();
  } else if (isGeoCard(card)) {
    return getGeoCardDataviz();
  } else if (isClassificationCard(card)) {
    return getClassificationCardDataviz();
  } else if (isDemographicCard(card)) {
    return getDemographicCardDataviz();
  } else if (isDateMetricsCard(card)) {
    return getDateMetricsCardDataviz();
  } else if (isTermMetricsCard(card)) {
    return getTermMetricsCardDataviz();
  } else if (isEmbeddedCard(card)) {
    return getEmbeddedCardDataviz();
  } else if (isTextCard(card)) {
    return getTextCardDataviz();
  } else if (isPinnedPostCard(card)) {
    return getPinnedPostDataviz();
  } else if (isPinnedAuthorCard(card)) {
    return getPinnedAuthorDataviz();
  } else if (isClusterCard(card)) {
    return getClusterCardDataviz();
  } else if (isEmvCard(card)) {
    return getEmvCardDataviz();
  } else if (isAICard(card)) {
    return getAICardDataviz();
  } else {
    return [];
  }
}

const DATAVIZ_SECTIONS = {
  basic: [
    DatavizName.HorizontalBarChart,
    DatavizName.DoughnutChart,
    DatavizName.Cloud,
    DatavizName.Map,
    DatavizName.Demographic,
    DatavizName.Kpi,
    DatavizName.LineChart,
  ],
  date: [
    DatavizName.MultipleLineChart,
    DatavizName.StreamChart,
    DatavizName.StackedBarChart,
  ],
  advanced: [
    DatavizName.HorizontalStackedBarChart,
    DatavizName.BubbleChart,
    DatavizName.ScatterPlot,
    DatavizName.Sunburst,
    DatavizName.TreeMap,
    DatavizName.DoubleTimeChart,
  ],
  none: [DatavizName.PictureWall, DatavizName.TopAuthors, DatavizName.TopPosts],
};

export function getDatavizSections(
  card: Card,
  facets: Record<string, FullFacet>,
  treeMapEnabled: boolean // feature flag to remove
) {
  const cardVizs = getAccessibleDatavizFromCard(card, facets);
  return reduce(
    DATAVIZ_SECTIONS as Record<string, Dataviz["name"][]>,
    (acc, section, id) => {
      let available = intersection(section, cardVizs);
      if (!treeMapEnabled) {
        // to remove when feature flag is done
        available = without(available, DatavizName.TreeMap);
      }
      if (available.length > 0) {
        acc.push({ title: id, values: available });
      }
      return acc;
    },
    []
  );
}

export function isDateMetricCardDataviz(
  dataviz: Dataviz
): dataviz is DateMetricCard["dataviz"] {
  return includes(getDateMetricDataviz(), dataviz.name);
}

export function isDateMetricsCardDataviz(
  dataviz: Dataviz
): dataviz is DateMetricsCard["dataviz"] {
  return includes(getDateMetricsCardDataviz(), dataviz.name);
}

export function isDateTermCardDataviz(
  dataviz: Dataviz
): dataviz is DateTermCard["dataviz"] {
  return includes(getDateTermCardDataviz(), dataviz.name);
}

export function isPostsCardDataviz(
  dataviz: Dataviz
): dataviz is PostsCard["dataviz"] {
  return includes(getPostsCardDataviz(), dataviz.name);
}

export function isClassificationCardDataviz(
  dataviz: Dataviz
): dataviz is ClassificationCard["dataviz"] {
  return includes(getClassificationCardDataviz(), dataviz.name);
}

export function isGeoCardDataviz(
  dataviz: Dataviz
): dataviz is GeoCard["dataviz"] {
  return includes(getGeoCardDataviz(), dataviz.name);
}

export function isTermCardDataviz(
  dataviz: Dataviz
): dataviz is TermCard["dataviz"] {
  return includes(getTermCardDataviz(), dataviz.name);
}

export function isTermMetricsCardDataviz(
  dataviz: Dataviz
): dataviz is TermMetricsCard["dataviz"] {
  return includes(getTermMetricsCardDataviz(), dataviz.name);
}

export function isDemographicCardDataviz(
  dataviz: Dataviz
): dataviz is DemographicCard["dataviz"] {
  return isDemographicDataviz(dataviz);
}

export function isUsersCardDataviz(
  dataviz: Dataviz
): dataviz is UsersCard["dataviz"] {
  return includes(getUsersCardDataviz(), dataviz.name);
}

export function isPivotCardDataviz(
  dataviz: Dataviz
): dataviz is PivotCard["dataviz"] {
  return includes(getPivotCardDataviz(), dataviz.name);
}

export function isClusterCardDataviz(
  dataviz: Dataviz
): dataviz is ClusterCard["dataviz"] {
  return includes(getClusterCardDataviz(), dataviz.name);
}

export function isColorOptionAvailable(card: Card) {
  if (isTermCard(card)) {
    return getTermFromCard(card) !== Facet.EmojiCharts;
  } else {
    return true;
  }
}

export function isEmbeddedCardDataviz(
  dataviz: Dataviz
): dataviz is EmbeddedCard["dataviz"] {
  return isEmbeddedDataviz(dataviz);
}

export function isTextCardDataviz(
  dataviz: Dataviz
): dataviz is TextCard["dataviz"] {
  return isTextDataviz(dataviz);
}

export function isEmvCardDataviz(
  dataviz: Dataviz
): dataviz is EmvCard["dataviz"] {
  return isEmvDataviz(dataviz);
}

export function isAICardDataviz(
  dataviz: Dataviz
): dataviz is AICard["dataviz"] {
  return isAIDataviz(dataviz);
}

export function isPinnedPostCardDataviz(
  dataviz: Dataviz
): dataviz is PinnedPostCard["dataviz"] {
  return isPinnedPostDataviz(dataviz);
}

export function isPinnedAuthorCardDataviz(
  dataviz: Dataviz
): dataviz is PinnedAuthorCard["dataviz"] {
  return isPinnedAuthorDataviz(dataviz);
}

export function isCardDataviz(card: Card, dataviz: Dataviz) {
  if (isTermCard(card)) {
    return isTermCardDataviz(dataviz);
  } else if (isClassificationCard(card)) {
    return isClassificationCardDataviz(dataviz);
  } else if (isGeoCard(card)) {
    return isGeoCardDataviz(dataviz);
  } else if (isDateTermCard(card)) {
    return isDateTermCardDataviz(dataviz);
  } else if (isDateMetricCard(card)) {
    return isDateMetricCardDataviz(dataviz);
  } else if (isUsersCard(card)) {
    return isUsersCardDataviz(dataviz);
  } else if (isDemographicCard(card)) {
    return isDemographicCardDataviz(dataviz);
  } else if (isTermMetricsCard(card)) {
    return isTermMetricsCardDataviz(dataviz);
  } else {
    return false;
  }
}

export function getDefaultTermMetricsCardDataviz(dataviz: Dataviz) {
  if (isTermMetricsCardDataviz(dataviz)) {
    return dataviz;
  } else {
    return getDefaultDataviz(
      DatavizName.BubbleChart
    ) as TermMetricsCard["dataviz"];
  }
}

export function getClassicColorFromTerm(term: string) {
  if (term === Facet.Platforms) return "platform";
  if (term === Facet.Tones) return "tone";
  if (term === Facet.Genders || term === Facet.ImageRecognitionGenders)
    return "gender";
  return "default";
}

const isFacetDefaultColor = (facet: FullFacet) => {
  const defaultFacets = [
    Facet.ImageRecognitionEmotions,
    Facet.Languages,
    Facet.Emotions,
    Facet.Focuses,
    Facet.ReviewRate,
  ];
  return (
    defaultFacets.includes(facet.id as Facet) || isClosedCustomField(facet)
  );
};

export function getColorFromFacet(facet: FullFacet) {
  if (!facet) return null;
  const { id } = facet;
  if (isFacetDefaultColor(facet)) return "default";
  if (id === Facet.Affects) return "affect";
  if (id === Facet.Platforms) return "platform";
  if (id === Facet.Tones) return "tone";
  if (id === Facet.Genders || id === Facet.ImageRecognitionGenders)
    return "gender";
  return null;
}

export function getColorFromTerm(
  datavizName: Dataviz["name"],
  term: string,
  facet?: FullFacet
) {
  if (
    datavizName === DatavizName.HorizontalBarChart ||
    datavizName === DatavizName.Cloud
  ) {
    return getColorFromFacet(facet);
  }
  return getClassicColorFromTerm(term);
}

export function isEmbeddedImageDataviz(dataviz: DatavizEmbedded) {
  return dataviz?.url?.match(/jpeg|jpg|gif|png$/) !== null;
}

export function isFullscreenScrollableDataviz(dataviz: Dataviz): boolean {
  if (!dataviz) {
    return false;
  }
  const { name = "" } = dataviz;
  return (
    name === DatavizName.HorizontalBarChart ||
    name === DatavizName.HorizontalStackedBarChart ||
    name === DatavizName.TopAuthors ||
    name === DatavizName.TopPosts
  );
}
